import { useContentResource } from "@ctv/core";
import { Document, DocumentFieldValue, Text } from "@ctv/shared-core/src";
import { Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { ViewStyle } from "react-native";
import {
  RequiredDocumentState,
  useRequiredDocument,
} from "../../../_contexts/RequiredDocumentContext";
import { RegistrationFormValue } from "../../../types";
import { DocumentRow } from "./DocumentRow";
import ReviewRow from "./ReviewRow";
import ReviewStack from "./ReviewStack";

type Props = {
  style?: ViewStyle;
  handleViewDocument(documentType: string, index?: number): void;
  type: keyof Pick<
    RequiredDocumentState,
    "documents" | "creditAssessmentDocuments"
  >;
};

export default function ReviewCompanyDocument({
  type: documentGroupType,
  style,
  handleViewDocument,
}: Props) {
  const { CorporateRegistration: cr } = useContentResource();
  const requiredDocument = useRequiredDocument();
  const { getValues } = useFormContext<RegistrationFormValue>();

  const [document, documentNotes] = getValues(["document", "documentNotes"]);
  const documents = Object.keys(document ?? []);
  const documentsLength = documents.filter((type) =>
    requiredDocument[documentGroupType].some(
      (document) => document.docType === type
    )
  ).length;

  if (documentsLength === 0) {
    return null;
  }

  return (
    <Fragment>
      <Text variant="title-1" style={style}>
        {cr.reviewDocumentSection}
      </Text>
      <ReviewStack>
        {documents.flatMap((type) => {
          const reqDoc = findDocument(
            requiredDocument[documentGroupType],
            type
          );

          if (!reqDoc) {
            return null;
          }

          const d = document![type];

          const documents = (
            Array.isArray(d) ? d : [d]
          ) as DocumentFieldValue[];

          // Intended to be looped in here
          // To render divider on CardStack for each multiple files
          return documents.filter(Boolean).map((document, index) => {
            let label: string | undefined = document.label;
            if (reqDoc.multiple && index > 0) {
              label = undefined;
            }

            return (
              <DocumentRow
                key={type + index}
                document={document}
                label={label}
                handleViewDocument={() =>
                  handleViewDocument(type, reqDoc.multiple ? index : undefined)
                }
              />
            );
          });
        })}
        <ReviewRow label={cr.documentNotesLabel} value={documentNotes} />
      </ReviewStack>
    </Fragment>
  );
}

// Recursive function to find a document inside the checkbox attribute
function findDocument(
  documents: Document[],
  typeToFind: string
): Document | undefined {
  return documents.find((document, index) => {
    const d = document.docType === typeToFind;

    if (!d && documents[index].checkbox) {
      return findDocument(documents[index].checkbox!.documents, typeToFind);
    }

    return d;
  });
}
